// Small presentational primitives shared by every estimate screen.
// Compact enterprise look: 4px radius, tight padding, theme tokens only,
// no hover motion/colour fades (see CLAUDE.md → Interaction & Motion Rules).

const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[4px] border font-medium select-none " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";

const BTN_VARIANTS = {
  primary: "bg-primary border-primary text-white",
  secondary: "bg-surface border-border text-text_primary",
  accent: "bg-secondary border-secondary text-white",
  danger: "bg-danger_bg border-danger_border text-danger",
  ghost: "bg-transparent border-transparent text-text_secondary",
  success: "bg-success_bg border-success_border text-success",
};

const BTN_SIZES = {
  xs: "h-7 px-2 text-[11.5px]",
  sm: "h-8 px-3 text-[12.5px]",
  md: "h-9 px-3.5 text-[13px]",
};

export function Button({ variant = "secondary", size = "sm", icon: Icon, iconRight: IconRight, className = "", children, ...props }) {
  return (
    <button type="button" className={`${BTN_BASE} ${BTN_VARIANTS[variant]} ${BTN_SIZES[size]} ${className}`} {...props}>
      {Icon && <Icon size={size === "xs" ? 13 : 14} className="flex-shrink-0" aria-hidden="true" />}
      {children}
      {IconRight && <IconRight size={13} className="flex-shrink-0 opacity-80" aria-hidden="true" />}
    </button>
  );
}

export function IconButton({ icon: Icon, label, size = "sm", variant = "secondary", className = "", ...props }) {
  const box = size === "xs" ? "h-7 w-7" : "h-8 w-8";
  return (
    <button type="button" aria-label={label} title={label} className={`${BTN_BASE} ${BTN_VARIANTS[variant]} ${box} ${className}`} {...props}>
      <Icon size={size === "xs" ? 13 : 15} aria-hidden="true" />
    </button>
  );
}

export function Card({ className = "", children, ...props }) {
  return (
    <section className={`rounded-[4px] border border-border bg-surface ${className}`} {...props}>
      {children}
    </section>
  );
}

export function CardHeader({ title, subtitle, icon: Icon, actions, className = "" }) {
  return (
    <header className={`flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 ${className}`}>
      <div className="flex min-w-0 items-center gap-2.5">
        {Icon && (
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[4px] bg-primary_tint text-primary">
            <Icon size={15} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="truncate text-[13.5px] font-semibold text-text_primary">{title}</h3>
          {subtitle && <p className="truncate text-[11.5px] text-text_muted">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

const PILL_TONES = {
  neutral: "bg-surface_alt border-border text-text_secondary",
  primary: "bg-primary_tint border-border text-primary",
  success: "bg-success_bg border-success_border text-success",
  warning: "bg-warning_bg border-warning_border text-warning",
  high: "bg-high_bg border-high_border text-high",
  danger: "bg-danger_bg border-danger_border text-danger",
  info: "bg-info_bg border-info_border text-info",
};

export function Pill({ tone = "neutral", dot = false, className = "", children }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-[1px] text-[11px] font-semibold ${PILL_TONES[tone]} ${className}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}

export const INPUT_CLS =
  "h-8 w-full rounded-[4px] border border-border bg-surface px-2.5 text-[13px] text-text_primary placeholder:text-text_muted " +
  "focus:outline-none focus:border-border_strong focus-visible:ring-2 focus-visible:ring-border disabled:opacity-60 disabled:cursor-not-allowed";

export function Field({ label, hint, error, htmlFor, required, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-text_secondary">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="text-[11px] text-danger">{error}</p> : hint ? <p className="text-[11px] text-text_muted">{hint}</p> : null}
    </div>
  );
}

export function TextInput({ className = "", hasError, ...props }) {
  return <input className={`${INPUT_CLS} ${hasError ? "border-danger" : ""} ${className}`} {...props} />;
}

export function NumberInput({ value, onChange, className = "", hasError, step = "any", ...props }) {
  return (
    <input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value === "" ? "" : Number(e.target.value))}
      className={`${INPUT_CLS} text-right tabular-nums ${hasError ? "border-danger" : ""} ${className}`}
      {...props}
    />
  );
}

export function TextArea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full rounded-[4px] border border-border bg-surface px-2.5 py-2 text-[13px] text-text_primary placeholder:text-text_muted focus:outline-none focus:border-border_strong focus-visible:ring-2 focus-visible:ring-border ${className}`}
      {...props}
    />
  );
}

export function Checkbox({ checked, onChange, label, disabled, className = "", ...props }) {
  return (
    <label className={`inline-flex items-center gap-2 text-[12.5px] text-text_primary ${disabled ? "opacity-60" : "cursor-pointer"} ${className}`}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-primary)]"
        {...props}
      />
      {label}
    </label>
  );
}

/**
 * Segmented control (filter chips, Y/N, brand mode). `options`: [{ value, label, count? }].
 */
export function Segmented({ options, value, onChange, size = "sm", ariaLabel, disabled }) {
  const h = size === "xs" ? "h-7 text-[11.5px]" : "h-8 text-[12.5px]";
  return (
    <div role="radiogroup" aria-label={ariaLabel} className="inline-flex rounded-[4px] border border-border bg-surface_alt p-0.5">
      {options.map((o) => {
        const active = String(o.value) === String(value);
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={`${h} inline-flex items-center gap-1.5 rounded-[3px] px-2.5 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong disabled:cursor-not-allowed ${
              active ? "bg-surface text-text_primary shadow-sm border border-border" : "text-text_secondary border border-transparent"
            }`}
          >
            {o.label}
            {o.count != null && (
              <span className={`rounded-full px-1.5 text-[10.5px] tabular-nums ${active ? "bg-primary text-white" : "bg-border_soft text-text_secondary"}`}>{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export const YES_NO = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

/** Horizontal progress bar (data-driven fill animation is allowed). */
export function ProgressBar({ pct, tone = "primary", className = "" }) {
  const fill = { primary: "bg-primary", success: "bg-success", warning: "bg-warning", secondary: "bg-secondary" }[tone];
  return (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-surface_alt border border-border_soft ${className}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className={`h-full ${fill} transition-[width] duration-300 ease-out`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

/** Table cell / header class helpers for the spreadsheet-like tables. */
export const TH = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-text_muted whitespace-nowrap bg-surface_alt border-b border-border";
export const TD = "px-3 py-2 text-[12.5px] text-text_primary border-b border-border_soft whitespace-nowrap";
export const TD_NUM = `${TD} text-right tabular-nums`;
